import React, { useEffect, useState } from 'react';
import { ArrowLeft, Edit, Loader2, ReceiptText, Save, SquarePen, Table2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import useAccountStore from '../../store/useAccountStore';
import Breadcrumb from '../../components/common/Breadcrumb';
import AccountStatement from '../../components/accounts/AccountStatement';
import PersonnelFormFields from '../../components/personnel/PersonnelFormFields';
import { formatCurrency } from '../../utils/formatters';

const emptyForm = {
  name: '',
  code: '',
  type: 'EMPLOYEE',
  employmentType: 'FIXED',
  personnelGroup: null,
  dailyRate: 0,
  currency: 'TRY',
  phone: '',
  email: '',
  status: 'Active',
  notes: ''
};

const normalizePersonnelFormData = (personnel = {}) => ({
  ...emptyForm,
  ...personnel,
  type: 'EMPLOYEE',
  employmentType: personnel.employmentType || 'FIXED'
});

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('tr-TR');
};

const STAGE_LABELS = {
  origin: 'Origin',
  freight: 'Freight',
  delivery: 'Delivery'
};

const PersonnelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    fetchAccountById,
    saveAccount,
    fetchAccountOperations,
    clearCurrentAccount,
    currentOperations,
    currentOperationsError,
    loading
  } = useAccountStore();

  const [personnel, setPersonnel] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const formatMinorCurrency = (value, currency = 'TRY') => formatCurrency(Number(value) || 0, currency);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!id || id === 'new' || id === 'undefined' || id === 'null') {
        navigate('/personnel', { replace: true });
        return;
      }

      clearCurrentAccount();
      const data = await fetchAccountById(id);
      if (!mounted) return;

      if (!data) {
        toast.error('Personel bulunamadı');
        navigate('/personnel', { replace: true });
        return;
      }

      setPersonnel(data);
      setFormData(normalizePersonnelFormData(data));
    };

    load();

    return () => {
      mounted = false;
    };
  }, [clearCurrentAccount, fetchAccountById, id, navigate]);

  useEffect(() => {
    if (!id || !personnel) return;

    if (activeTab === 'operations') {
      fetchAccountOperations(id);
    }
  }, [activeTab, fetchAccountOperations, id, personnel]);

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);

    const result = await saveAccount({
      ...normalizePersonnelFormData(formData),
      type: 'EMPLOYEE'
    }, id);

    setSaving(false);

    if (result.success) {
      const savedPersonnel = result.data || personnel;
      setPersonnel(savedPersonnel);
      setFormData(normalizePersonnelFormData(savedPersonnel));
      setIsEditing(false);
      toast.success('Personel güncellendi');
      return;
    }

    toast.error(result.message || 'Personel güncellenemedi');
  };

  const handleCancelEdit = () => {
    setFormData(normalizePersonnelFormData(personnel || {}));
    setIsEditing(false);
  };

  if (!personnel && loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="rounded-[2rem] border border-slate-100 bg-white p-8 text-center font-bold text-slate-400 shadow-sm">
          Yükleniyor...
        </div>
      </div>
    );
  }

  if (!personnel) return null;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Breadcrumb
        items={[
          { label: 'Personel Listesi', href: '/personnel' },
          { label: personnel.name || 'Personel Detayı' }
        ]}
      />

      <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/personnel')}
            className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800"
          >
            <ArrowLeft size={14} />
            Personel Listesi
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-black text-slate-800 tracking-tight">{personnel.name}</h1>
            <span className="rounded-full bg-blue-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-blue-700">
              {personnel.employmentType || 'FIXED'}
            </span>
            <span className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest ${
              personnel.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
            }`}>
              {personnel.status === 'Active' ? 'Aktif' : 'Pasif'}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            {personnel.code || 'Kodsuz'} • {personnel.phone || personnel.email || 'İletişim bilgisi yok'}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-black"
            >
              <Edit size={14} />
              Düzenle
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition-colors hover:bg-slate-200"
              >
                <X size={14} />
                İptal
              </button>
              <button
                type="submit"
                form="personnel-edit-form"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Kaydet
              </button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-wrap gap-2">
              {[
                { id: 'info', label: 'Bilgiler', icon: SquarePen },
                { id: 'statement', label: 'Hesap Ekstresi', icon: ReceiptText },
                { id: 'operations', label: 'Operasyonlar', icon: Table2 }
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-black transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-50 text-slate-500 hover:bg-white hover:text-slate-800'
                    }`}
                  >
                    <Icon size={14} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'info' && (
              isEditing ? (
                <form id="personnel-edit-form" onSubmit={handleSave} className="space-y-6">
                  <PersonnelFormFields
                    formData={formData}
                    setFormData={setFormData}
                    showStatus
                    showNotes
                    showCode
                  />
                </form>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[
                    ['Ad Soyad', personnel.name],
                    ['Kod', personnel.code || '-'],
                    ['Personel Tipi', personnel.employmentType || '-'],
                    ['Telefon', personnel.phone || '-'],
                    ['E-posta', personnel.email || '-'],
                    ['Durum', personnel.status || '-']
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
                      <p className="mt-2 text-sm font-black text-slate-800 break-words">{value}</p>
                    </div>
                  ))}
                  <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5 md:col-span-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Notlar</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm font-medium text-slate-700">
                      {personnel.notes || '-'}
                    </p>
                  </div>
                </div>
              )
            )}

            {activeTab === 'statement' && (
              <AccountStatement accountId={personnel._id} variant="personnel" currentLanguage="tr" />
            )}

            {activeTab === 'operations' && (
              <div className="overflow-hidden rounded-[1.75rem] border border-slate-100 bg-slate-50/50">
                <div className="border-b border-slate-100 bg-white px-5 py-4">
                  <h3 className="text-base font-black text-slate-800">Operasyon Süreçleri</h3>
                  <p className="text-xs font-bold text-slate-400">Personelin görev aldığı Origin / Freight / Delivery süreçleri</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <th className="p-4">Operasyon Kodu / Adı</th>
                        <th className="p-4">Müşteri</th>
                        <th className="p-4">Süreç / Aşama</th>
                        <th className="p-4">İşlem Tarihi</th>
                        <th className="p-4 text-right">Aşama Hakedişi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {currentOperations.map((row) => {
                        const operationId = row.operationId?._id || row.operationId;
                        return (
                          <tr
                            key={row._id || `${operationId}-${row.stage}`}
                            className={`bg-white ${operationId ? 'cursor-pointer hover:bg-slate-50/80' : ''}`}
                            onClick={() => {
                              if (operationId) navigate(`/operations/${operationId}`);
                            }}
                          >
                            <td className="p-4">
                              <p className="font-black text-slate-800">{row.projectNo || '-'}</p>
                              <p className="text-[10px] font-bold text-slate-400">
                                {[row.operationType, row.serviceType].filter(Boolean).join(' • ') || '-'}
                              </p>
                            </td>
                            <td className="p-4">
                              <p className="text-sm font-black text-slate-800">{row.customerName || row.account?.name || '-'}</p>
                              <p className="text-[10px] font-bold text-slate-400">{row.customerCode || row.account?.code || ''}</p>
                            </td>
                            <td className="p-4">
                              <span className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-700">
                                {STAGE_LABELS[row.stage] || row.stage || '-'}
                              </span>
                            </td>
                            <td className="p-4 text-sm font-bold text-slate-600">
                              {formatDate(row.date || row.createdAt)}
                            </td>
                            <td className="p-4 text-right text-sm font-black text-slate-700">
                              {formatMinorCurrency(row.totalAmount, row.currency || 'TRY')}
                            </td>
                          </tr>
                        );
                      })}
                      {currentOperations.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-16 text-center text-slate-400 font-bold">
                            {currentOperationsError || 'Bu personel için henüz operasyon süreci bulunmuyor.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
      </div>
    </div>
  );
};

export default PersonnelDetail;
